import "./Style.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { MdDelete } from "react-icons/md";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import { Form, Row, Col } from "react-bootstrap";
import ButtonCom from "../../components/button/button";
import Spinner from "react-bootstrap/Spinner";
import { PiPencilSimpleLineBold } from "react-icons/pi";
import Modal from "react-bootstrap/Modal";
import Switch from "@mui/material/Switch";
import { useDispatch, useSelector } from "react-redux";
import {
  commonSaveData,
  commonGetData,
  initState,
  initCommonState,
} from "../../redux/slices/commonSlice";
import {
  updateLeavePath,
  deleteLeavePath,
  getLeavePath,
  getModuleLeaveList,
  insertLeavePath,
} from "../../services/config";
import Widgets from "../../components/Widgets";
import WidgetsIcon from "../../components/Widgets/icon";
import { useGlobalToast } from "../../stopOldToastGlobally";

// ─── InputWithButtons ─────────────────────────────────────────────────────────
const InputWithButtons = ({
  value, onChange, name, placeholder = "0", min = 0, max = 999,
  showUnitDropdown = false, unit = "days", onUnitChange, maxLength = 3,
  textColor, borderColor, inputBackground, mutedTextColor, accentColor,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => { setInputValue(value); }, [value]);

  const handleInputChange = (e) => {
    let v = e.target.value;
    if (v === "") { setInputValue(""); return; }
    if (!/^\d*$/.test(v)) return;
    if (v.length > maxLength) v = v.slice(0, maxLength);
    setInputValue(v);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const num = Math.max(min, Math.min(max, parseInt(inputValue || "0") || 0));
    const fv = num.toString();
    setInputValue(fv);
    onChange({ target: { name, value: fv } });
  };

  const handleKeyDown = (e) => {
    if (
      ["Backspace","Delete","Tab","Escape","Enter","ArrowLeft","ArrowRight","Home","End"].includes(e.key) ||
      (e.key >= "0" && e.key <= "9")
    ) return;
    e.preventDefault();
  };

  const incr = () => {
    const v = Math.min(parseInt(inputValue || "0") + 1, max).toString();
    setInputValue(v);
    onChange({ target: { name, value: v } });
  };

  const decr = () => {
    const c = parseInt(inputValue || "0");
    if (c > min) {
      const v = (c - 1).toString();
      setInputValue(v);
      onChange({ target: { name, value: v } });
    }
  };

  const bdr = `1px solid ${isFocused ? accentColor : borderColor}`;
  const bBase = {
    height: "40px", border: "none", background: "transparent", color: textColor,
    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
    fontSize: "20px", fontWeight: "bold", transition: "all 0.2s ease", flexShrink: 0,
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", background: inputBackground,
      borderRadius: "8px", border: bdr, overflow: "hidden", transition: "all 0.2s ease",
      minWidth: showUnitDropdown ? "240px" : "180px",
    }}>
      <button type="button" onClick={decr} style={{
        ...bBase, width: "36px", borderRight: bdr,
        opacity: parseInt(inputValue || "0") <= min ? 0.5 : 1,
        pointerEvents: parseInt(inputValue || "0") <= min ? "none" : "auto",
      }}><span>-</span></button>
      <div style={{ position: "relative", flex: 1, height: "40px" }}>
        <input
          type="text" inputMode="numeric" name={name} value={inputValue}
          onChange={handleInputChange} onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)} onBlur={handleBlur}
          placeholder={placeholder} maxLength={maxLength}
          style={{
            width: "100%", height: "100%", padding: "0 8px", textAlign: "center",
            background: "transparent", color: textColor, border: "none",
            fontSize: "0.95rem", fontWeight: 600, outline: "none",
          }}
        />
      </div>
      <button type="button" onClick={incr} style={{
        ...bBase, width: "36px", borderLeft: bdr,
        opacity: parseInt(inputValue || "0") >= max ? 0.5 : 1,
        pointerEvents: parseInt(inputValue || "0") >= max ? "none" : "auto",
      }}><span>+</span></button>
      {showUnitDropdown ? (
        <Form.Select value={unit} onChange={onUnitChange} style={{
          width: "120px", height: "40px", padding: "0 8px", background: "transparent",
          color: textColor, border: "none", borderLeft: bdr, fontSize: "0.85rem",
          fontWeight: 500, cursor: "pointer", outline: "none", flexShrink: 0,
        }}>
          <option value="days">Days</option>
          <option value="percentage">Percentage</option>
        </Form.Select>
      ) : (
        <span style={{
          color: mutedTextColor, fontSize: "0.85rem", padding: "0 10px",
          display: "flex", alignItems: "center", height: "40px", fontWeight: 500,
          borderLeft: bdr, minWidth: "45px", flexShrink: 0,
        }}>days</span>
      )}
    </div>
  );
};

// ─── CombinedInputWithDropdown ────────────────────────────────────────────────
const CombinedInputWithDropdown = ({
  value, unit, onValueChange, onUnitChange, label, hasError = false,
  maxLength = 3, textColor, borderColor, inputBackground, mutedTextColor, accentColor,
}) => {
  const [inputValue, setInputValue] = useState(String(value ?? "0"));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => { setInputValue(String(value ?? "0")); }, [value]);

  const handleInputChange = (e) => {
    let v = e.target.value;
    if (v === "") { setInputValue(""); return; }
    if (!/^\d*$/.test(v)) return;
    if (v.length > maxLength) v = v.slice(0, maxLength);
    setInputValue(v);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const fv = Math.min(parseInt(inputValue || "0") || 0, 999).toString();
    setInputValue(fv);
    onValueChange(fv);
  };

  const handleKeyDown = (e) => {
    if (
      ["Backspace","Delete","Tab","Escape","Enter","ArrowLeft","ArrowRight","Home","End"].includes(e.key) ||
      (e.key >= "0" && e.key <= "9")
    ) return;
    e.preventDefault();
  };

  const incr = (e) => {
    e.preventDefault(); e.stopPropagation();
    const next = Math.min(parseInt(inputValue || "0") + 1, 999);
    const v = next.toString();
    setInputValue(v); onValueChange(v);
  };

  const decr = (e) => {
    e.preventDefault(); e.stopPropagation();
    const cur = parseInt(inputValue || "0");
    if (cur > 0) { const v = (cur - 1).toString(); setInputValue(v); onValueChange(v); }
  };

  const ab = hasError ? "#ef4444" : isFocused ? accentColor : borderColor;
  const bBase = {
    height: "40px", border: "none", background: "transparent", color: textColor,
    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
    fontSize: "20px", fontWeight: "bold", transition: "all 0.2s ease", flexShrink: 0,
  };

  return (
    <div style={{ flex: 1, maxWidth: "280px" }}>
      <label style={{ color: mutedTextColor, fontSize: "0.85rem", fontWeight: 500, display: "block", marginBottom: "8px" }}>
        {label} <span style={{ color: "#ef4444" }}>*</span>
      </label>
      <div style={{
        display: "flex", alignItems: "center", background: inputBackground,
        borderRadius: "8px", border: `1px solid ${ab}`, overflow: "hidden",
        transition: "all 0.2s ease", width: "fit-content",
      }}>
        <button type="button" onClick={decr} style={{
          ...bBase, width: "32px", borderRight: `1px solid ${ab}`,
          opacity: parseInt(inputValue || "0") <= 0 ? 0.5 : 1,
          pointerEvents: parseInt(inputValue || "0") <= 0 ? "none" : "auto",
        }}><span>-</span></button>
        <div style={{ position: "relative", width: "50px", height: "40px" }}>
          <input
            type="text" inputMode="numeric" value={inputValue}
            onChange={handleInputChange} onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)} onBlur={handleBlur} maxLength={maxLength}
            style={{
              width: "100%", height: "100%", padding: "0 4px", textAlign: "center",
              background: "transparent", color: textColor, border: "none",
              fontSize: "0.95rem", fontWeight: 600, outline: "none",
            }}
          />
        </div>
        <button type="button" onClick={incr} style={{
          ...bBase, width: "32px", borderLeft: `1px solid ${ab}`,
          opacity: parseInt(inputValue || "0") >= 999 ? 0.5 : 1,
          pointerEvents: parseInt(inputValue || "0") >= 999 ? "none" : "auto",
        }}><span>+</span></button>
        <Form.Select value={unit} onChange={(e) => onUnitChange(e.target.value)} style={{
          width: "110px", height: "40px", padding: "0 8px", background: "transparent",
          color: textColor, border: "none", borderLeft: `1px solid ${ab}`,
          fontSize: "0.85rem", fontWeight: 500, cursor: "pointer", outline: "none", flexShrink: 0,
        }}>
          <option value="days">Days</option>
          <option value="percentage">Percentage</option>
        </Form.Select>
      </div>
      {hasError && (
        <div style={{ color: "#ef4444", fontSize: "0.75rem", marginTop: "4px", fontWeight: 500 }}>
          This field is required
        </div>
      )}
    </div>
  );
};

// ─── ToggleRow ────────────────────────────────────────────────────────────────
const ToggleRow = ({ label, value, onChange, accentColor, cardBackground, textColor, borderColor, mutedTextColor }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 14px", background: cardBackground, borderRadius: "8px",
    border: `1px solid ${borderColor}`, marginBottom: "8px",
  }}>
    <span style={{ color: textColor, fontSize: "0.88rem", fontWeight: 500 }}>{label}</span>
    <div style={{ display: "flex", gap: "6px" }}>
      {["Yes", "No"].map((opt) => {
        const isActive = opt === "Yes" ? value : !value;
        return (
          <button key={opt} type="button" onClick={() => onChange(opt === "Yes")}
            style={{
              padding: "4px 14px", borderRadius: "6px",
              border: `1px solid ${isActive ? accentColor : borderColor}`,
              background: isActive ? accentColor : "transparent",
              color: isActive ? "#fff" : mutedTextColor,
              fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", transition: "all 0.15s ease",
            }}>{opt}</button>
        );
      })}
    </div>
  </div>
);

// ─── FieldRow ─────────────────────────────────────────────────────────────────
const FieldRow = ({ label, children, mutedTextColor }) => (
  <div style={{ marginBottom: "10px" }}>
    <label style={{
      display: "block", color: mutedTextColor, fontSize: "0.78rem", fontWeight: 700,
      marginBottom: "6px", letterSpacing: "0.5px",
    }}>
      {label}
    </label>
    {children}
  </div>
);

// ─── SmallNumInput ────────────────────────────────────────────────────────────
const SmallNumInput = ({ value, onChange, placeholder = "0", textColor, borderColor, inputBackground, accentColor }) => {
  const [v, setV] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => setV(value), [value]);
  const commit = () => {
    const n = Math.max(0, parseInt(v || "0") || 0);
    setV(String(n)); onChange(String(n));
  };
  return (
    <div style={{
      display: "flex", alignItems: "center", background: inputBackground,
      borderRadius: "8px", border: `1px solid ${focused ? accentColor : borderColor}`,
      overflow: "hidden", width: "fit-content",
    }}>
      <button type="button"
        onClick={() => { const n = Math.max(0, parseInt(v || "0") - 1); setV(String(n)); onChange(String(n)); }}
        style={{ width: "30px", height: "36px", border: "none", background: "transparent", color: textColor, fontSize: "18px", fontWeight: "bold", cursor: "pointer" }}>
        -
      </button>
      <input
        type="text" inputMode="numeric" value={v}
        onChange={e => { if (/^\d*$/.test(e.target.value)) setV(e.target.value); }}
        onFocus={() => setFocused(true)} onBlur={commit} placeholder={placeholder}
        style={{
          width: "52px", height: "36px", textAlign: "center", background: "transparent",
          color: textColor, border: "none", fontSize: "0.9rem", fontWeight: 600, outline: "none",
        }}
      />
      <button type="button"
        onClick={() => { const n = parseInt(v || "0") + 1; setV(String(n)); onChange(String(n)); }}
        style={{ width: "30px", height: "36px", border: "none", background: "transparent", color: textColor, fontSize: "18px", fontWeight: "bold", cursor: "pointer" }}>
        +
      </button>
    </div>
  );
};

// ─── SmallSelect ──────────────────────────────────────────────────────────────
const SmallSelect = ({ value, onChange, options, textColor, borderColor, inputBackground }) => (
  <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
    <select
      value={value} onChange={e => onChange(e.target.value)}
      style={{
        padding: "8px 32px 8px 12px", borderRadius: "8px", border: `1px solid ${borderColor}`,
        background: inputBackground, color: textColor, fontSize: "0.88rem",
        fontWeight: 500, outline: "none", cursor: "pointer", appearance: "none",
        width: "100%",
      }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <svg
      style={{
        position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
        pointerEvents: "none", width: "16px", height: "16px",
      }}
      xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={textColor}
    >
      <path d="M7 10l5 5 5-5z" />
    </svg>
  </div>
);

// ─── SectionDivider ───────────────────────────────────────────────────────────
const SectionDivider = ({ label, borderColor, mutedTextColor }) => (
  <div style={{ display: "flex", alignItems: "center", margin: "18px 0 14px" }}>
    <div style={{ flex: 1, height: "1px", background: borderColor }} />
    <span style={{
      margin: "0 12px", color: mutedTextColor, fontSize: "0.72rem",
      fontWeight: 700, letterSpacing: "0.7px",
    }}>{label}</span>
    <div style={{ flex: 1, height: "1px", background: borderColor }} />
  </div>
);

// ─── CustomSalaryChips ────────────────────────────────────────────────────────
const CustomSalaryChips = ({ components, accentColor, isDark, onEdit }) => {
  if (!components || components.length === 0) return null;
  return (
    <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
      {components.map((c) => (
        <span key={c.key} style={{
          fontSize: "0.73rem", padding: "3px 10px", borderRadius: "20px",
          background: isDark ? "rgba(139,92,246,0.18)" : "rgba(124,58,237,0.08)",
          color: accentColor, fontWeight: 600,
          border: `1px solid ${isDark ? "rgba(139,92,246,0.35)" : "rgba(124,58,237,0.25)"}`,
          whiteSpace: "nowrap",
        }}>
          {c.label}{c.percentage ? ` · ${c.percentage}%` : ""}
        </span>
      ))}
      <button
        type="button"
        onClick={onEdit}
        style={{
          fontSize: "0.73rem", padding: "3px 10px", borderRadius: "20px",
          border: `1px dashed ${accentColor}`, background: "transparent",
          color: accentColor, cursor: "pointer", fontWeight: 600,
        }}
      >
        Edit
      </button>
    </div>
  );
};

// ─── DEFAULT_PAY_COMPONENTS ───────────────────────────────────────────────────
const DEFAULT_PAY_COMPONENTS = [
  { key: "basic_pay",          label: "Basic pay" },
  { key: "hra",                label: "HRA" },
  { key: "dearness_allowance", label: "Dearness allowance (DA)" },
  { key: "conveyance",         label: "Conveyance allowance" },
  { key: "medical",            label: "Medical allowance" },
  { key: "special",            label: "Special allowance" },
  { key: "bonus",              label: "Bonus / Incentives" },
];

// ─── CustomSalaryModal ────────────────────────────────────────────────────────
// Rendered via Portal so it always sits above all other stacking contexts
const CustomSalaryModal = ({
  open, onClose, onSave,
  selected = [],
  textColor, borderColor, inputBackground, mutedTextColor,
  accentColor, cardBackground, isDark,
}) => {
  const [checked,    setChecked]    = useState({});
  const [percents,   setPercents]   = useState({});
  const [customList, setCustomList] = useState([]);
  const [newName,    setNewName]    = useState("");
  const [addingNew,  setAddingNew]  = useState(false);

  // Sync state every time the modal opens
  useEffect(() => {
    if (!open) return;
    const c = {}, p = {};
    selected.forEach((s) => { c[s.key] = true; p[s.key] = s.percentage || ""; });
    setChecked(c);
    setPercents(p);
    const knownKeys = DEFAULT_PAY_COMPONENTS.map((d) => d.key);
    const extras = selected.filter((s) => !knownKeys.includes(s.key));
    setCustomList(extras.map((s) => ({ key: s.key, label: s.label })));
    setNewName("");
    setAddingNew(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const allComponents = [...DEFAULT_PAY_COMPONENTS, ...customList];

  const toggle = (key) => setChecked((p) => ({ ...p, [key]: !p[key] }));

  const handlePercent = (key, val) => {
    if (!/^\d*\.?\d*$/.test(val)) return;
    setPercents((p) => ({ ...p, [key]: val }));
  };

  const handleAddCustom = () => {
    const name = newName.trim();
    if (!name) return;
    const key = "custom_" + name.toLowerCase().replace(/\s+/g, "_") + "_" + Date.now();
    setCustomList((p) => [...p, { key, label: name }]);
    setChecked((p) => ({ ...p, [key]: true }));
    setPercents((p) => ({ ...p, [key]: "" }));
    setNewName("");
    setAddingNew(false);
  };

  const handleSave = () => {
    const result = allComponents
      .filter((c) => checked[c.key])
      .map((c) => ({ key: c.key, label: c.label, percentage: percents[c.key] || "" }));
    onSave(result);
    onClose();
  };

  const selectedCount = allComponents.filter((c) => checked[c.key]).length;

  if (!open) return null;

  // ── Portal to document.body so no parent stacking context interferes ──────
  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
        backdropFilter: "blur(2px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: cardBackground, borderRadius: "16px",
          border: `1px solid ${borderColor}`,
          width: "100%", maxWidth: "520px",
          maxHeight: "88vh", display: "flex", flexDirection: "column",
          boxShadow: "0 32px 80px rgba(0,0,0,0.45)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 22px", borderBottom: `1px solid ${borderColor}`,
          flexShrink: 0,
        }}>
          <div>
            <p style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: textColor }}>
              Custom salary components
            </p>
            <p style={{ margin: "3px 0 0", fontSize: "0.8rem", color: mutedTextColor }}>
              Select components and enter % of basic salary
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: mutedTextColor, fontSize: "22px", lineHeight: 1,
              padding: "2px 8px", borderRadius: "6px",
              transition: "color 0.15s",
            }}
          >✕</button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div style={{ overflowY: "auto", padding: "16px 22px", flex: 1 }}>
          {allComponents.map((comp) => (
            <div
              key={comp.key}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", marginBottom: "8px", borderRadius: "10px",
                border: `1px solid ${checked[comp.key] ? accentColor + "66" : borderColor}`,
                background: checked[comp.key]
                  ? isDark ? "rgba(139,92,246,0.09)" : "rgba(124,58,237,0.04)"
                  : inputBackground,
                transition: "all 0.18s",
                cursor: "pointer",
              }}
            >
              {/* Checkbox + label */}
              <div
                onClick={() => toggle(comp.key)}
                style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}
              >
                <div style={{
                  width: "18px", height: "18px", borderRadius: "5px", flexShrink: 0,
                  border: `2px solid ${checked[comp.key] ? accentColor : borderColor}`,
                  background: checked[comp.key] ? accentColor : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s",
                }}>
                  {checked[comp.key] && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span style={{
                  fontSize: "0.88rem", fontWeight: 500, color: textColor,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {comp.label}
                </span>
              </div>

              {/* Percentage input — visible only when checked */}
              {checked[comp.key] && (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0, marginLeft: "12px" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="text" inputMode="decimal"
                    value={percents[comp.key] || ""}
                    onChange={(e) => handlePercent(comp.key, e.target.value)}
                    placeholder="0"
                    style={{
                      width: "64px", padding: "5px 8px", textAlign: "center",
                      borderRadius: "7px", border: `1px solid ${borderColor}`,
                      background: inputBackground, color: textColor,
                      fontSize: "0.88rem", fontWeight: 600, outline: "none",
                    }}
                  />
                  <span style={{ fontSize: "0.82rem", color: mutedTextColor, fontWeight: 500 }}>%</span>
                </div>
              )}
            </div>
          ))}

          {/* Add custom component row */}
          {addingNew ? (
            <div style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "10px 14px", borderRadius: "10px",
              border: `1.5px dashed ${accentColor}`,
              background: isDark ? "rgba(139,92,246,0.06)" : "rgba(124,58,237,0.03)",
              marginBottom: "8px",
            }}>
              <input
                autoFocus
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddCustom();
                  if (e.key === "Escape") { setAddingNew(false); setNewName(""); }
                }}
                placeholder="Component name..."
                style={{
                  flex: 1, padding: "7px 10px", borderRadius: "7px",
                  border: `1px solid ${borderColor}`, background: inputBackground,
                  color: textColor, fontSize: "0.88rem", outline: "none",
                }}
              />
              <button
                onClick={handleAddCustom}
                style={{
                  padding: "7px 16px", borderRadius: "7px", border: "none",
                  background: accentColor, color: "#fff", fontSize: "0.82rem",
                  fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                }}
              >Add</button>
              <button
                onClick={() => { setAddingNew(false); setNewName(""); }}
                style={{
                  padding: "7px 12px", borderRadius: "7px",
                  border: `1px solid ${borderColor}`, background: "transparent",
                  color: mutedTextColor, fontSize: "0.82rem", cursor: "pointer",
                }}
              >Cancel</button>
            </div>
          ) : (
            <button
              onClick={() => setAddingNew(true)}
              style={{
                width: "100%", padding: "10px", borderRadius: "10px",
                border: `1.5px dashed ${borderColor}`, background: "transparent",
                color: accentColor, fontSize: "0.85rem", fontWeight: 600,
                cursor: "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", gap: "6px", transition: "border-color 0.18s",
                marginBottom: "4px",
              }}
            >
              <span style={{ fontSize: "18px", lineHeight: 1 }}>+</span>
              Add custom component
            </button>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div style={{
          padding: "14px 22px", borderTop: `1px solid ${borderColor}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
          background: cardBackground,
        }}>
          <span style={{ fontSize: "0.82rem", color: mutedTextColor }}>
            {selectedCount} component{selectedCount !== 1 ? "s" : ""} selected
          </span>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={onClose}
              style={{
                padding: "8px 18px", borderRadius: "8px",
                border: `1px solid ${borderColor}`, background: "transparent",
                color: textColor, fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
              }}
            >Cancel</button>
            <button
              onClick={handleSave}
              style={{
                padding: "8px 20px", borderRadius: "8px", border: "none",
                background: accentColor, color: "#fff",
                fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
              }}
            >Save components</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── SpecialLeaveCard ─────────────────────────────────────────────────────────
const SpecialLeaveCard = ({
  title, subtitle, badge, badgeColor, enabled, onToggle, children,
  cardBackground, innerBackground, borderColor, textColor,
  mutedTextColor, accentColor, isDark,
}) => (
  <div style={{
    borderRadius: "16px",
    border: `1px solid ${enabled ? accentColor + "66" : borderColor}`,
    overflow: "hidden", transition: "all 0.3s ease",
    boxShadow: enabled ? `0 4px 24px ${accentColor}1a` : "none",
    background: cardBackground,
  }}>
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "18px 24px",
      background: enabled
        ? isDark ? "rgba(139,92,246,0.1)" : "rgba(124,58,237,0.05)"
        : innerBackground,
      borderBottom: enabled ? `1px solid ${accentColor}33` : "1px solid transparent",
      transition: "all 0.3s ease",
    }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
          <span style={{ fontSize: "1.05rem", color: textColor, fontWeight: 700 }}>{title}</span>
          {badge && (
            <span style={{
              fontSize: "0.65rem", fontWeight: 700, padding: "2px 10px", borderRadius: "20px",
              background: badgeColor?.bg || (isDark ? "rgba(34,197,94,0.18)" : "rgba(34,197,94,0.1)"),
              color: badgeColor?.text || "#16a34a",
            }}>{badge}</span>
          )}
        </div>
        <span style={{ fontSize: "0.83rem", color: mutedTextColor }}>{subtitle}</span>
      </div>
      <Switch
        checked={enabled} onChange={onToggle}
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": { color: accentColor },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: accentColor },
        }}
      />
    </div>
    {enabled && (
      <div style={{ padding: "24px", background: cardBackground }}>
        {children}
      </div>
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const LeaveSettings = () => {
  const params   = useParams();
  const dispatch = useDispatch();
  const state    = useSelector((s) => s.common);
  const Toast    = useGlobalToast();

  const themeStore = useSelector((s) => s.theme);
  const isDark = themeStore?.theme === "dark" || !themeStore?.theme;

  const user = localStorage.getItem("user_data")
    ? JSON.parse(localStorage.getItem("user_data"))
    : null;

  const [carryforwardUnit, setCarryforwardUnit] = useState("days");
  const [encashmentUnit,   setEncashmentUnit]   = useState("days");

  const updatePath  = updateLeavePath;
  const deletePath  = deleteLeavePath;
  const getPath     = `${getLeavePath}${params.id}`;
  const getListPath = `${getModuleLeaveList}${params.id}`;

  const [open,       setOpen]       = useState(false);
  const [validated,  setValidated]  = useState(false);
  const [leaveList,  setLeaveList]  = useState([]);
  const [isLoading,  setIsLoading]  = useState(false);
  const [saveAction, setSaveAction] = useState("none");
  const getRequestTypeRef = useRef("settings");

  const savePhaseRef        = useRef("idle");
  const pendingCFPayloadRef = useRef(null);
  const combinedCheckedRef  = useRef(false);
  const combinedRulesRef    = useRef({
    carryforwardValue: "0", carryforwardUnit: "days",
    encashmentValue: "0",   encashmentUnit: "days",
  });

  const [carryforwardChecked,    setCarryforwardChecked]    = useState(false);
  const [encashmentChecked,      setEncashmentChecked]      = useState(false);
  const [allCarryforwardChecked, setAllCarryforwardChecked] = useState(false);
  const [allEncashmentChecked,   setAllEncashmentChecked]   = useState(false);
  const [combinedRulesChecked,   setCombinedRulesChecked]   = useState(false);

  const individualRulesActive = carryforwardChecked || encashmentChecked;
  const combinedRulesActive   = combinedRulesChecked;

  // ── Menstrual Leave state ────────────────────────────────────────────────────
  const [menstrualEnabled,         setMenstrualEnabled]         = useState(false);
  const [menstrualDays,            setMenstrualDays]            = useState("");
  const [menstrualResetCycle,      setMenstrualResetCycle]      = useState(false);
  const [menstrualMaxPerCycle,     setMenstrualMaxPerCycle]     = useState("0");
  const [menstrualHalfDay,         setMenstrualHalfDay]         = useState(false);
  const [menstrualCarryForward,    setMenstrualCarryForward]    = useState(false);
  const [menstrualEncashment,      setMenstrualEncashment]      = useState(false);
  const [menstrualClubbingAllowed, setMenstrualClubbingAllowed] = useState(false);
  const [menstrualApprovalRequired,setMenstrualApprovalRequired]= useState(false);
  const [menstrualReasonMandatory, setMenstrualReasonMandatory] = useState(false);

  // ── Maternity Leave state ────────────────────────────────────────────────────
  const [maternityEnabled,         setMaternityEnabled]         = useState(false);
  const [maternityDays,            setMaternityDays]            = useState("");
  const [maternityDurationWeeks,   setMaternityDurationWeeks]   = useState("0");
  const [maternityNumChildren,     setMaternityNumChildren]     = useState("0");
  const [maternityPreDelivery,     setMaternityPreDelivery]     = useState("0");
  const [maternityPostAutoAlloc,   setMaternityPostAutoAlloc]   = useState(false);
  const [maternityPaidStatus,      setMaternityPaidStatus]      = useState("paid");
  const [maternitySalaryBasis,     setMaternitySalaryBasis]     = useState("full_pay");
  const [maternityCarryForward,    setMaternityCarryForward]    = useState(false);
  const [maternityEncashment,      setMaternityEncashment]      = useState(false);
  const [maternityClubbingAllowed, setMaternityClubbingAllowed] = useState(false);
  const [maternityMedCertRequired, setMaternityMedCertRequired] = useState(false);
  const [maternityDocUpload,       setMaternityDocUpload]       = useState(false);
  const [maternityApprovalRequired,setMaternityApprovalRequired]= useState(false);

  // ── Paternity Leave state ────────────────────────────────────────────────────
  const [paternityEnabled,              setPaternityEnabled]              = useState(false);
  const [paternityDays,                 setPaternityDays]                 = useState("");
  const [paternityTotalDays,            setPaternityTotalDays]            = useState("0");
  const [paternityUsageWindow,          setPaternityUsageWindow]          = useState("0");
  const [paternityContinuousRequired,   setPaternityContinuousRequired]   = useState(false);
  const [paternitySplitAllowed,         setPaternitySplitAllowed]         = useState(false);
  const [paternityPaidStatus,           setPaternityPaidStatus]           = useState("paid");
  const [paternitySalaryBasis,          setPaternitySalaryBasis]          = useState("full_pay");
  const [paternityCarryForward,         setPaternityCarryForward]         = useState(false);
  const [paternityEncashment,           setPaternityEncashment]           = useState(false);
  const [paternityClubbingAllowed,      setPaternityClubbingAllowed]      = useState(false);
  const [paternityMedCertRequired,      setPaternityMedCertRequired]      = useState(false);
  const [paternityDocUpload,            setPaternityDocUpload]            = useState(false);
  const [paternityApprovalRequired,     setPaternityApprovalRequired]     = useState(false);

  // ── Custom salary modal state ────────────────────────────────────────────────
  const [showCustomSalaryModal, setShowCustomSalaryModal] = useState(false);
  const [customSalaryTarget,    setCustomSalaryTarget]    = useState(null); // "maternity" | "paternity"
  const [maternityCustomPay,    setMaternityCustomPay]    = useState([]);
  const [paternityCustomPay,    setPaternityCustomPay]    = useState([]);

  // ── Combined Rules ───────────────────────────────────────────────────────────
  const syncSetCombinedRulesChecked = (val) => {
    combinedCheckedRef.current = val;
    setCombinedRulesChecked(val);
  };

  const syncSetCombinedRules = (updater) => {
    setCombinedRules((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      combinedRulesRef.current = next;
      return next;
    });
  };

  const [combinedRules, setCombinedRules] = useState({
    carryforwardValue: "0", carryforwardUnit: "days",
    encashmentValue: "0",   encashmentUnit: "days",
  });

  const [carryforward, setCarryforward] = useState({
    userId: user?.user_id || "", leaveId: "", leaveName: "",
    periodFrom: "", periodTo: "",
    leaveCarryforward: "1", allLeaveCarryForward: "0",
    allowMaxLeaveCarryForward: "",
    leaveEncashment: "0", allLeaveEncashment: "0", allowMaxLeaveEncashment: "0",
  });

  const [leaveData, setLeaveData] = useState({
    userId: user?.user_id || "", leaveId: "", leaveName: "",
    leaveBalance: "", leavePeriod: "1", periodFrom: "", periodTo: "",
  });

  const [show,               setShow]               = useState(false);
  const [isEdit,             setIsEdit]             = useState(false);
  const [delId,              setDelId]              = useState(null);
  const [carryforwardSubmit, setCarryforwardSubmit] = useState(false);
  const [editingLeaveId,     setEditingLeaveId]     = useState(null);
  const [editingLeaveName,   setEditingLeaveName]   = useState("");

  const resetCombined = () => {
    const empty = {
      carryforwardValue: "0", carryforwardUnit: "days",
      encashmentValue: "0",   encashmentUnit: "days",
    };
    combinedRulesRef.current = empty;
    setCombinedRules(empty);
  };

  const handleToggleCarryforward = (e) => {
    const on = e.target.checked;
    if (on && combinedRulesChecked) { syncSetCombinedRulesChecked(false); resetCombined(); }
    setCarryforwardChecked(on);
    setCarryforward((p) => ({
      ...p, leaveCarryforward: "1",
      allLeaveCarryForward: on ? p.allLeaveCarryForward : "0",
    }));
    if (!on) setAllCarryforwardChecked(false);
  };

  const handleToggleAllCarryforward = (e) => {
    const on = e.target.checked;
    setAllCarryforwardChecked(on);
    setCarryforward((p) => ({ ...p, allLeaveCarryForward: on ? "1" : "0" }));
  };

  const handleToggleEncashment = (e) => {
    const on = e.target.checked;
    if (on && combinedRulesChecked) { syncSetCombinedRulesChecked(false); resetCombined(); }
    setEncashmentChecked(on);
    setCarryforward((p) => ({
      ...p, leaveEncashment: on ? "1" : "0",
      allLeaveEncashment: on ? p.allLeaveEncashment : "0",
    }));
    if (!on) setAllEncashmentChecked(false);
  };

  const handleToggleAllEncashment = (e) => {
    const on = e.target.checked;
    setAllEncashmentChecked(on);
    setCarryforward((p) => ({ ...p, allLeaveEncashment: on ? "1" : "0" }));
  };

  const handleToggleCombinedRules = (e) => {
    const on = e.target.checked;
    if (on) {
      setCarryforwardChecked(false); setEncashmentChecked(false);
      setAllCarryforwardChecked(false); setAllEncashmentChecked(false);
      setCarryforward((p) => ({ ...p, allLeaveCarryForward: "0", allLeaveEncashment: "0" }));
    } else { resetCombined(); }
    syncSetCombinedRulesChecked(on);
  };

  const handleCombinedRulesChange = (field, value) => {
    syncSetCombinedRules((p) => ({ ...p, [field]: String(value) }));
  };

  const handleLeaveChanges = (e) => {
    const { name, value } = e.target;
    setCarryforward((p) => ({ ...p, [name]: value }));
  };

  const handlePeriodChange = (e) => {
    const { name, value } = e.target;
    setCarryforward((p) => ({ ...p, [name]: value }));
  };

  const getLeaveList = useCallback(() => {
    setIsLoading(true);
    getRequestTypeRef.current = "settings";
    dispatch(commonGetData(getPath));
  }, [getPath, dispatch]);

  const getModuleLeaveListData = useCallback(() => {
    setIsLoading(true);
    getRequestTypeRef.current = "list";
    dispatch(commonGetData(getListPath));
  }, [getListPath, dispatch]);

  useEffect(() => { getLeaveList(); }, [getLeaveList]);

  const normalizeLeave = (l) => ({
    leaveId:                      String(l.leaveId   ?? l.leave_id   ?? ""),
    leaveName:                    String(l.leaveName  ?? l.leave_name ?? ""),
    leaveBalance:                 String(l.leaveBalance ?? l.leave_balance ?? "0"),
    leave_carryforward:           String(l.leave_carryforward           ?? "0"),
    all_leave_carryforward:       String(l.all_leave_carryforward       ?? "0"),
    allow_max_leave_carryforward: String(l.allow_max_leave_carryforward ?? "0"),
    leave_encashment:             String(l.leave_encashment             ?? "0"),
    all_leave_encashment:         String(l.all_leave_encashment         ?? "0"),
    allow_max_leave_encashment:   String(l.allow_max_leave_encashment   ?? "0"),
  });

  useEffect(() => {
    if (!state.commonGetSuccess) return;
    const rawData = state.commonGetData;
    setIsLoading(false);

    if (getRequestTypeRef.current === "list") {
      const listData = rawData?.data || rawData;
      let rawLeaves = [];
      if      (Array.isArray(listData))                                      rawLeaves = listData;
      else if (listData?.leaves      && Array.isArray(listData.leaves))      rawLeaves = listData.leaves;
      else if (listData?.leaveModule && Array.isArray(listData.leaveModule)) rawLeaves = listData.leaveModule;
      else if (listData?.leaveList   && Array.isArray(listData.leaveList))   rawLeaves = listData.leaveList;
      else if (Array.isArray(rawData))                                       rawLeaves = rawData;
      else if (rawData?.leaves       && Array.isArray(rawData.leaves))       rawLeaves = rawData.leaves;
      if (rawLeaves.length > 0) setLeaveList(rawLeaves.map(normalizeLeave));
      dispatch(initState());
      return;
    }

    const data            = rawData?.data || rawData;
    const periodFromValue = String(data?.periodFrom ?? data?.period_from ?? "");
    const periodToValue   = String(data?.periodTo   ?? data?.period_to   ?? "");

    let rawLeaves = [];
    if      (Array.isArray(data?.leaves))      rawLeaves = data.leaves;
    else if (Array.isArray(data?.leaveModule)) rawLeaves = data.leaveModule;
    else if (Array.isArray(data?.leaveList))   rawLeaves = data.leaveList;
    else if (Array.isArray(data))              rawLeaves = data;
    else if (Array.isArray(rawData?.leaves))   rawLeaves = rawData.leaves;
    else if (Array.isArray(rawData))           rawLeaves = rawData;

    const leaves = rawLeaves.map(normalizeLeave);

    const combinedEnabled   = data?.isCombinedRulesAllow;
    const combinedCfwdValue = (data?.craLeaveCarryforward && data.craLeaveCarryforward !== "") ? String(data.craLeaveCarryforward) : "0";
    const combinedEncsValue = (data?.craleaveEncashment && data.craleaveEncashment !== "")     ? String(data.craleaveEncashment)   : "0";
    const combinedCfwdUnit  = data?.combinedCarryforwardUnit || "days";
    const combinedEncsUnit  = data?.combinedEncashmentUnit   || "days";
    const isCombinedOn = combinedEnabled === "1" || combinedEnabled === 1 || combinedEnabled === true;

    // Restore Menstrual
    if (data?.menstrualLeave !== undefined) {
      setMenstrualEnabled(data.menstrualLeave === "1" || data.menstrualLeave === 1);
      setMenstrualDays(data.menstrualLeaveDays && data.menstrualLeaveDays !== "0" ? String(data.menstrualLeaveDays) : "");
      setMenstrualResetCycle(data.menstrualResetCycle === "1" || data.menstrualResetCycle === 1);
      setMenstrualMaxPerCycle(String(data.menstrualMaxPerCycle ?? "0"));
      setMenstrualHalfDay(data.menstrualHalfDay === "1" || data.menstrualHalfDay === 1);
      setMenstrualCarryForward(data.menstrualCarryForward === "1" || data.menstrualCarryForward === 1);
      setMenstrualEncashment(data.menstrualEncashment === "1" || data.menstrualEncashment === 1);
      setMenstrualClubbingAllowed(data.menstrualClubbingAllowed === "1" || data.menstrualClubbingAllowed === 1);
      setMenstrualApprovalRequired(data.menstrualApprovalRequired === "1" || data.menstrualApprovalRequired === 1);
      setMenstrualReasonMandatory(data.menstrualReasonMandatory === "1" || data.menstrualReasonMandatory === 1);
    }

    // Restore Maternity
    if (data?.maternityLeave !== undefined) {
      setMaternityEnabled(data.maternityLeave === "1" || data.maternityLeave === 1);
      setMaternityDays(data.maternityLeaveDays && data.maternityLeaveDays !== "0" ? String(data.maternityLeaveDays) : "");
      setMaternityDurationWeeks(String(data.maternityDurationWeeks ?? "0"));
      setMaternityNumChildren(String(data.maternityNumChildren ?? "0"));
      setMaternityPreDelivery(String(data.maternityPreDelivery ?? "0"));
      setMaternityPostAutoAlloc(data.maternityPostAutoAlloc === "1" || data.maternityPostAutoAlloc === 1);
      setMaternityPaidStatus(data.maternityPaidStatus || "paid");
      setMaternitySalaryBasis(data.maternitySalaryBasis || "full_pay");
      setMaternityCarryForward(data.maternityCarryForward === "1" || data.maternityCarryForward === 1);
      setMaternityEncashment(data.maternityEncashment === "1" || data.maternityEncashment === 1);
      setMaternityClubbingAllowed(data.maternityClubbingAllowed === "1" || data.maternityClubbingAllowed === 1);
      setMaternityMedCertRequired(data.maternityMedCertRequired === "1" || data.maternityMedCertRequired === 1);
      setMaternityDocUpload(data.maternityDocUpload === "1" || data.maternityDocUpload === 1);
      setMaternityApprovalRequired(data.maternityApprovalRequired === "1" || data.maternityApprovalRequired === 1);
      // Restore custom pay components
      if (data.maternityCustomPayComponents) {
        try { setMaternityCustomPay(JSON.parse(data.maternityCustomPayComponents)); } catch {}
      }
    }

    // Restore Paternity
    if (data?.paternityLeave !== undefined) {
      setPaternityEnabled(data.paternityLeave === "1" || data.paternityLeave === 1);
      setPaternityDays(data.paternityLeaveDays && data.paternityLeaveDays !== "0" ? String(data.paternityLeaveDays) : "");
      setPaternityTotalDays(String(data.paternityTotalDays ?? "0"));
      setPaternityUsageWindow(String(data.paternityUsageWindow ?? "0"));
      setPaternityContinuousRequired(data.paternityContinuousRequired === "1" || data.paternityContinuousRequired === 1);
      setPaternitySplitAllowed(data.paternitySplitAllowed === "1" || data.paternitySplitAllowed === 1);
      setPaternityPaidStatus(data.paternityPaidStatus || "paid");
      setPaternitySalaryBasis(data.paternitySalaryBasis || "full_pay");
      setPaternityCarryForward(data.paternityCarryForward === "1" || data.paternityCarryForward === 1);
      setPaternityEncashment(data.paternityEncashment === "1" || data.paternityEncashment === 1);
      setPaternityClubbingAllowed(data.paternityClubbingAllowed === "1" || data.paternityClubbingAllowed === 1);
      setPaternityMedCertRequired(data.paternityMedCertRequired === "1" || data.paternityMedCertRequired === 1);
      setPaternityDocUpload(data.paternityDocUpload === "1" || data.paternityDocUpload === 1);
      setPaternityApprovalRequired(data.paternityApprovalRequired === "1" || data.paternityApprovalRequired === 1);
      // Restore custom pay components
      if (data.paternityCustomPayComponents) {
        try { setPaternityCustomPay(JSON.parse(data.paternityCustomPayComponents)); } catch {}
      }
    }

    const targetLeave =
      leaves.find((v) => v.leaveId && v.leaveId !== "" &&
        (v.leave_carryforward === "1" || v.leave_encashment === "1")) ||
      leaves.find((v) => v.leaveId && v.leaveId !== "") ||
      leaves[0] || null;

    if (targetLeave) {
      setCarryforward({
        userId:    user?.user_id || "",
        leaveId:   targetLeave.leaveId,
        leaveName: targetLeave.leaveName,
        periodFrom: periodFromValue, periodTo: periodToValue,
        leaveCarryforward: "1",
        allLeaveCarryForward: targetLeave.all_leave_carryforward || "0",
        allowMaxLeaveCarryForward:
          targetLeave.allow_max_leave_carryforward && targetLeave.allow_max_leave_carryforward !== "0"
            ? targetLeave.allow_max_leave_carryforward : "0",
        leaveEncashment:         targetLeave.leave_encashment       || "0",
        allLeaveEncashment:      targetLeave.all_leave_encashment    || "0",
        allowMaxLeaveEncashment: targetLeave.allow_max_leave_encashment || "0",
      });
      setCarryforwardChecked(!isCombinedOn && targetLeave.leave_carryforward === "1");
      setAllCarryforwardChecked(!isCombinedOn && targetLeave.all_leave_carryforward === "1");
      setEncashmentChecked(!isCombinedOn && (targetLeave.leave_encashment === "1" || targetLeave.leave_encashment === "2"));
      setAllEncashmentChecked(!isCombinedOn && targetLeave.all_leave_encashment === "1");
      if (data?.carryforwardUnit) setCarryforwardUnit(data.carryforwardUnit);
      if (data?.encashmentUnit)   setEncashmentUnit(data.encashmentUnit);
    } else {
      setCarryforward((p) => ({
        ...p, periodFrom: periodFromValue, periodTo: periodToValue,
        leaveCarryforward: "1", allLeaveCarryForward: "0",
        allowMaxLeaveCarryForward: "0", leaveEncashment: "0",
        allLeaveEncashment: "0", allowMaxLeaveEncashment: "0",
      }));
      setCarryforwardChecked(false); setAllCarryforwardChecked(false);
      setEncashmentChecked(false);   setAllEncashmentChecked(false);
    }

    setLeaveData((p) => ({ ...p, leavePeriod: String(data?.leavePeriod ?? data?.leave_period ?? "1") }));
    setLeaveList(leaves);
    syncSetCombinedRulesChecked(isCombinedOn);

    if (isCombinedOn) {
      const restored = {
        carryforwardValue: combinedCfwdValue, carryforwardUnit: combinedCfwdUnit,
        encashmentValue: combinedEncsValue,   encashmentUnit: combinedEncsUnit,
      };
      combinedRulesRef.current = restored;
      setCombinedRules(restored);
    } else {
      resetCombined();
    }

    dispatch(initState());
  }, [state.commonGetSuccess, state.commonGetData, user?.user_id, dispatch]);

  useEffect(() => {
    if (!state.commonSuccess) return;
    const action = saveAction;

    if (action === "saveSettings") {
      if (savePhaseRef.current === "modulePhase") {
        if (pendingCFPayloadRef.current) {
          savePhaseRef.current = "carryforwardPhase";
          dispatch(initState());
          dispatch(commonSaveData({ path: updatePath, data: pendingCFPayloadRef.current }));
        } else {
          savePhaseRef.current = "idle";
          pendingCFPayloadRef.current = null;
          setSaveAction("none");
          Toast.success("Leave settings saved successfully");
          dispatch(initState());
          getLeaveList();
        }
        return;
      }
      savePhaseRef.current = "idle";
      pendingCFPayloadRef.current = null;
      setSaveAction("none");
      Toast.success(state.commonMessage || "Leave settings saved successfully");
      dispatch(initState());
      getLeaveList();
      return;
    }

    if (action === "leaveModule") {
      Toast.success(state.commonMessage || "Leave type saved successfully");
      setCarryforwardSubmit(false);
      setLeaveData((prev) => ({
        userId: user?.user_id || "", leaveId: "", leaveName: "",
        leaveBalance: "", leavePeriod: prev.leavePeriod, periodFrom: "", periodTo: "",
      }));
      setValidated(false); setOpen(false); setSaveAction("none");
      dispatch(initState()); getLeaveList();
      return;
    }

    if (action === "delete") {
      Toast.success(state.commonMessage || "Leave type deleted successfully");
      setSaveAction("none"); dispatch(initState()); getLeaveList();
      return;
    }

    Toast.success(state.commonMessage || "Saved successfully");
    setSaveAction("none"); dispatch(initState());
  }, [state.commonSuccess, state.commonMessage, dispatch, Toast, saveAction, getLeaveList, user?.user_id, updatePath]);

  useEffect(() => {
    if (!state.commonError) return;
    if (saveAction === "saveSettings") {
      if (savePhaseRef.current === "modulePhase") {
        Toast.error(state.commonMessage || "Failed to save leave settings");
      } else if (savePhaseRef.current === "carryforwardPhase") {
        const msg = state.commonMessage || "";
        const isNoChanges = msg.toLowerCase().includes("no changes") || msg.toLowerCase().includes("no change");
        if (isNoChanges) { Toast.success("Leave balances saved successfully"); }
        else { Toast.error("Leave balances saved. Carryforward settings update failed."); }
        getLeaveList();
      }
      savePhaseRef.current = "idle";
      pendingCFPayloadRef.current = null;
    } else {
      Toast.error(state.commonMessage || "Operation failed");
    }
    dispatch(initCommonState());
    setSaveAction("none"); setCarryforwardSubmit(false); setIsLoading(false);
  }, [state.commonError, state.commonMessage, dispatch, Toast, saveAction, getLeaveList]);

  const buildCarryforwardOnlyPayload = useCallback(
    (resolvedLeaveId, resolvedLeaveName) => {
      const isCombinedOn = combinedCheckedRef.current;
      const snapRules    = combinedRulesRef.current;
      return {
        userId:    String(user?.user_id || "0"),
        leaveId:   String(resolvedLeaveId),
        leaveName: String(resolvedLeaveName),
        periodFrom: String(carryforward.periodFrom || ""),
        periodTo:   String(carryforward.periodTo   || ""),
        leaveCarryforward:         "1",
        allLeaveCarryForward:      (!isCombinedOn && allCarryforwardChecked) ? "1" : "0",
        allowMaxLeaveCarryForward: (!isCombinedOn && carryforward.allowMaxLeaveCarryForward) ? String(carryforward.allowMaxLeaveCarryForward) : "0",
        leaveEncashment:         (!isCombinedOn && encashmentChecked) ? "1" : "0",
        allLeaveEncashment:      (!isCombinedOn && allEncashmentChecked) ? "1" : "0",
        allowMaxLeaveEncashment: (!isCombinedOn && carryforward.allowMaxLeaveEncashment) ? String(carryforward.allowMaxLeaveEncashment) : "0",
        isCombinedRulesAllow: isCombinedOn ? "1" : "0",
        craLeaveCarryforward: String(snapRules.carryforwardValue || "0"),
        craleaveEncashment:   String(snapRules.encashmentValue   || "0"),
        // Menstrual
        menstrualLeave:             menstrualEnabled ? "1" : "0",
        menstrualLeaveDays:         menstrualEnabled ? String(menstrualDays || "0") : "0",
        menstrualResetCycle:        menstrualResetCycle ? "1" : "0",
        menstrualMaxPerCycle:       String(menstrualMaxPerCycle || "0"),
        menstrualHalfDay:           menstrualHalfDay ? "1" : "0",
        menstrualCarryForward:      menstrualCarryForward ? "1" : "0",
        menstrualEncashment:        menstrualEncashment ? "1" : "0",
        menstrualClubbingAllowed:   menstrualClubbingAllowed ? "1" : "0",
        menstrualApprovalRequired:  menstrualApprovalRequired ? "1" : "0",
        menstrualReasonMandatory:   menstrualReasonMandatory ? "1" : "0",
        // Maternity
        maternityLeave:             maternityEnabled ? "1" : "0",
        maternityLeaveDays:         maternityEnabled ? String(maternityDays || "0") : "0",
        maternityDurationWeeks:     String(maternityDurationWeeks || "0"),
        maternityNumChildren:       String(maternityNumChildren || "0"),
        maternityPreDelivery:       String(maternityPreDelivery || "0"),
        maternityPostAutoAlloc:     maternityPostAutoAlloc ? "1" : "0",
        maternityPaidStatus:        maternityPaidStatus,
        maternitySalaryBasis:       maternitySalaryBasis,
        maternityCarryForward:      maternityCarryForward ? "1" : "0",
        maternityEncashment:        maternityEncashment ? "1" : "0",
        maternityClubbingAllowed:   maternityClubbingAllowed ? "1" : "0",
        maternityMedCertRequired:   maternityMedCertRequired ? "1" : "0",
        maternityDocUpload:         maternityDocUpload ? "1" : "0",
        maternityApprovalRequired:  maternityApprovalRequired ? "1" : "0",
        maternityCustomPayComponents: JSON.stringify(maternityCustomPay),
        // Paternity
        paternityLeave:             paternityEnabled ? "1" : "0",
        paternityLeaveDays:         paternityEnabled ? String(paternityDays || "0") : "0",
        paternityTotalDays:         String(paternityTotalDays || "0"),
        paternityUsageWindow:       String(paternityUsageWindow || "0"),
        paternityContinuousRequired: paternityContinuousRequired ? "1" : "0",
        paternitySplitAllowed:      paternitySplitAllowed ? "1" : "0",
        paternityPaidStatus:        paternityPaidStatus,
        paternitySalaryBasis:       paternitySalaryBasis,
        paternityCarryForward:      paternityCarryForward ? "1" : "0",
        paternityEncashment:        paternityEncashment ? "1" : "0",
        paternityClubbingAllowed:   paternityClubbingAllowed ? "1" : "0",
        paternityMedCertRequired:   paternityMedCertRequired ? "1" : "0",
        paternityDocUpload:         paternityDocUpload ? "1" : "0",
        paternityApprovalRequired:  paternityApprovalRequired ? "1" : "0",
        paternityCustomPayComponents: JSON.stringify(paternityCustomPay),
      };
    },
    [
      user?.user_id,
      carryforward.periodFrom, carryforward.periodTo,
      carryforward.allowMaxLeaveCarryForward, carryforward.allowMaxLeaveEncashment,
      allCarryforwardChecked, encashmentChecked, allEncashmentChecked,
      menstrualEnabled, menstrualDays, menstrualResetCycle, menstrualMaxPerCycle,
      menstrualHalfDay, menstrualCarryForward, menstrualEncashment,
      menstrualClubbingAllowed, menstrualApprovalRequired, menstrualReasonMandatory,
      maternityEnabled, maternityDays, maternityDurationWeeks, maternityNumChildren,
      maternityPreDelivery, maternityPostAutoAlloc, maternityPaidStatus, maternitySalaryBasis,
      maternityCarryForward, maternityEncashment, maternityClubbingAllowed,
      maternityMedCertRequired, maternityDocUpload, maternityApprovalRequired, maternityCustomPay,
      paternityEnabled, paternityDays, paternityTotalDays, paternityUsageWindow,
      paternityContinuousRequired, paternitySplitAllowed, paternityPaidStatus, paternitySalaryBasis,
      paternityCarryForward, paternityEncashment, paternityClubbingAllowed,
      paternityMedCertRequired, paternityDocUpload, paternityApprovalRequired, paternityCustomPay,
    ]
  );

  const handleClickOpen = () => {
    setIsEdit(false); setValidated(false);
    setLeaveData({
      userId: user?.user_id || "", leaveId: "", leaveName: "",
      leaveBalance: "", leavePeriod: leaveData.leavePeriod, periodFrom: "", periodTo: "",
    });
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    setLeaveData((prev) => ({
      userId: user?.user_id || "", leaveId: "", leaveName: "",
      leaveBalance: "", leavePeriod: prev.leavePeriod, periodFrom: "", periodTo: "",
    }));
    setValidated(false); setOpen(false);
  }, [user?.user_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLeaveData((prev) => ({ ...prev, [name]: value }));
  };

  const doAddLeave = () => {
    const leaveName    = leaveData.leaveName?.trim() || "";
    const leaveBalance = leaveData.leaveBalance?.toString().trim() || "";
    if (!leaveName)    { setValidated(true); Toast.error("Leave name is required."); return; }
    if (!leaveBalance) { setValidated(true); Toast.error("Leave balance is required."); return; }

    const existingModule = leaveList.map((l) => ({
      ...(l.leaveId && l.leaveId !== "" ? { leaveId: String(l.leaveId) } : {}),
      leaveName: l.leaveName, leaveBalance: String(l.leaveBalance || "0"),
    }));

    const isCombinedOn = combinedCheckedRef.current;
    const snapRules    = combinedRulesRef.current;

    const payload = {
      moduleId: String(params.id), userId: String(user?.user_id || "0"),
      leaveName, leaveBalance: String(leaveBalance),
      leavePeriod: String(leaveData.leavePeriod || "1"),
      periodFrom: String(carryforward.periodFrom || ""),
      periodTo:   String(carryforward.periodTo   || ""),
      leaveCarryforward:         "1",
      allLeaveCarryForward:      (!isCombinedOn && allCarryforwardChecked) ? "1" : "0",
      allowMaxLeaveCarryForward: (!isCombinedOn && carryforward.allowMaxLeaveCarryForward) ? String(carryforward.allowMaxLeaveCarryForward) : "0",
      leaveEncashment:           (!isCombinedOn && encashmentChecked) ? "1" : "0",
      allLeaveEncashment:        (!isCombinedOn && allEncashmentChecked) ? "1" : "0",
      allowMaxLeaveEncashment:   (!isCombinedOn && carryforward.allowMaxLeaveEncashment) ? String(carryforward.allowMaxLeaveEncashment) : "0",
      isCombinedRulesAllow: isCombinedOn ? "1" : "0",
      craLeaveCarryforward: String(snapRules.carryforwardValue || "0"),
      craleaveEncashment:   String(snapRules.encashmentValue   || "0"),
      leaveModule: [...existingModule, { leaveName, leaveBalance: String(leaveBalance) }],
    };

    setSaveAction("leaveModule");
    dispatch(commonSaveData({ path: insertLeavePath, data: payload }));
  };

  const preSubmit = () => {
    if (isEdit) { const el = document.querySelector("#newLeaveId"); if (el) el.click(); }
    else doAddLeave();
  };

  const handleSubmit = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!isEdit) doAddLeave();
  };

  const handleEditSubmit = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.currentTarget.checkValidity() === false) { setValidated(true); return; }
    if (!params.id) { Toast.error("Module ID is required"); return; }
    if (!leaveData.leaveName || !leaveData.leaveBalance) {
      Toast.error("Leave name and balance are required"); return;
    }

    const updatedList = leaveList.map((l) =>
      l.leaveId === leaveData.leaveId
        ? { ...l, leaveName: leaveData.leaveName.trim(), leaveBalance: leaveData.leaveBalance.toString() }
        : l
    );

    const payload = {
      moduleId: String(params.id), leavePeriod: String(leaveData.leavePeriod || "1"),
      leaveModule: updatedList.map((l) => ({
        ...(l.leaveId && l.leaveId !== "" ? { leaveId: String(l.leaveId) } : {}),
        leaveName: l.leaveName, leaveBalance: String(l.leaveBalance || "0"),
      })),
    };

    setSaveAction("leaveModule");
    dispatch(commonSaveData({ path: insertLeavePath, data: payload }));
  };

  const handleUpdate = () => {
    if (saveAction !== "none") return;
    if (leaveList.length === 0) { Toast.error("Please add at least one leave type first"); return; }

    const resolvedLeaveId   = carryforward.leaveId || leaveList[0]?.leaveId || "";
    const resolvedLeaveName = carryforward.leaveName || leaveList[0]?.leaveName || "";

    pendingCFPayloadRef.current = resolvedLeaveId
      ? buildCarryforwardOnlyPayload(resolvedLeaveId, resolvedLeaveName)
      : null;

    const modulePayload = {
      moduleId: String(params.id),
      leavePeriod: String(leaveData.leavePeriod || "1"),
      leaveModule: leaveList.map((l) => ({
        ...(l.leaveId && l.leaveId !== "" ? { leaveId: String(l.leaveId) } : {}),
        leaveName: l.leaveName, leaveBalance: String(l.leaveBalance ?? "0"),
      })),
    };

    savePhaseRef.current = "modulePhase";
    setSaveAction("saveSettings");
    dispatch(commonSaveData({ path: insertLeavePath, data: modulePayload }));
  };

  const handleDelete      = (id) => { setDelId(id); setShow(true); };
  const handleModalClose  = () => setShow(false);
  const handleDeleteSubmit = () => {
    setSaveAction("delete");
    dispatch(commonSaveData({
      path: deletePath,
      data: { userId: String(user?.user_id || ""), leaveId: String(delId || "") },
    }));
    setShow(false);
  };

  const handleEdit = (row) => {
    setIsEdit(true); setValidated(false);
    setLeaveData({
      userId: user?.user_id, leaveId: row.leaveId, leaveName: row.leaveName,
      leaveBalance: row.leaveBalance, leavePeriod: leaveData.leavePeriod || row.leavePeriod || "1",
      periodFrom: "", periodTo: "",
    });
    setOpen(true);
  };

  const handelArrayChange = (e, index) => {
    const { name, value } = e.target;
    const arr = [...leaveList];
    arr[index] = { ...arr[index], [name]: value };
    setLeaveList(arr);
  };

  const handleLeaveNameClick    = (row) => { setEditingLeaveId(row.leaveId); setEditingLeaveName(row.leaveName); };
  const handleLeaveNameChange   = (e)   => setEditingLeaveName(e.target.value);
  const handleLeaveNameKeyPress = (e, idx) => { if (e.key === "Enter") { e.preventDefault(); handleLeaveNameBlur(idx); } };
  const handleLeaveNameBlur     = (index) => {
    if (editingLeaveName.trim()) {
      const arr = [...leaveList];
      arr[index] = { ...arr[index], leaveName: editingLeaveName.trim() };
      setLeaveList(arr);
    }
    setEditingLeaveId(null); setEditingLeaveName("");
  };

  // ── Helper: open modal for a given target ────────────────────────────────────
  const openCustomModal = (target) => {
    setCustomSalaryTarget(target);
    setShowCustomSalaryModal(true);
  };

  // ── Helper: handle salary basis change (auto-open modal on "custom") ─────────
  const handleMaternitySalaryBasisChange = (val) => {
    setMaternitySalaryBasis(val);
    if (val === "custom") openCustomModal("maternity");
  };

  const handlePaternitySalaryBasisChange = (val) => {
    setPaternitySalaryBasis(val);
    if (val === "custom") openCustomModal("paternity");
  };

  // ── Theme ────────────────────────────────────────────────────────────────────
  const backgroundColor    = isDark ? "#0a0514"               : "#f5f7fa";
  const cardBackground     = isDark ? "#1a1429"               : "#ffffff";
  const textColor          = isDark ? "#ffffff"               : "#1a202c";
  const lightTextColor     = isDark ? "#e0e0ff"               : "#2d3748";
  const borderColor        = isDark ? "#2a2342"               : "#e2e8f0";
  const inputBackground    = isDark ? "#211C35"               : "#E7E7E7";
  const headerBackground   = isDark ? "#1a1429"               : "#ffffff";
  const mutedTextColor     = isDark ? "#9ca3af"               : "#718096";
  const sectionBorderColor = isDark ? "#2a2342"               : "#e2e8f0";
  const innerBackground    = isDark ? "#0f0b1a"               : "#f7fafc";
  const switchBorderColor  = isDark ? "#3d3358"               : "#cbd5e0";
  const selectBackground   = isDark ? "#0f0b1a"               : "#ffffff";
  const accentColor        = isDark ? "#8b5cf6"               : "#7c3aed";
  const addCardBackground  = isDark ? "rgba(139,92,246,0.1)"  : "rgba(124,58,237,0.05)";
  const addCardBorder      = isDark ? "rgba(139,92,246,0.3)"  : "rgba(124,58,237,0.2)";
  const disabledBorder     = isDark ? "rgba(139,92,246,0.25)" : "rgba(124,58,237,0.2)";

  const tp      = { textColor, borderColor, inputBackground, mutedTextColor, accentColor };
  const trProps = { accentColor, cardBackground: innerBackground, textColor, borderColor, mutedTextColor };
  const snProps = { textColor, borderColor, inputBackground, accentColor };

  const getLeavePeriodText = () => leaveData.leavePeriod === "1" ? "year" : "month";

  if (isLoading && leaveList.length === 0) {
    return (
      <Widgets
        icon={<WidgetsIcon icon={<Link to="/leave-module" className="icon-color"><ArrowBackIcon /></Link>} title="Leave Settings" />}
        body={
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
            <Spinner animation="border" variant="primary" />
            <span style={{ marginLeft: "10px", color: textColor }}>Loading settings...</span>
          </div>
        }
      />
    );
  }

  return (
    <>
      {/* ── Portal modal — always rendered at body level ───────────────────── */}
      <CustomSalaryModal
        open={showCustomSalaryModal}
        onClose={() => setShowCustomSalaryModal(false)}
        onSave={(components) => {
          if (customSalaryTarget === "maternity") setMaternityCustomPay(components);
          if (customSalaryTarget === "paternity") setPaternityCustomPay(components);
        }}
        selected={
          customSalaryTarget === "maternity" ? maternityCustomPay :
          customSalaryTarget === "paternity" ? paternityCustomPay : []
        }
        textColor={textColor} borderColor={borderColor}
        inputBackground={inputBackground} mutedTextColor={mutedTextColor}
        accentColor={accentColor} cardBackground={cardBackground} isDark={isDark}
      />

      <Widgets
        icon={<WidgetsIcon icon={<Link to="/leave-module" className="icon-color"><ArrowBackIcon /></Link>} title="Leave Settings" />}
        body={
          <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto", backgroundColor: backgroundColor, minHeight: "100vh" }}>

            {/* ── Header / Period selector ──────────────────────────────────── */}
            <div style={{
              display: "flex", flexWrap: "wrap", alignItems: "center", gap: "16px",
              padding: "20px 24px", background: headerBackground, borderRadius: "16px",
              marginBottom: "32px", border: `1px solid ${borderColor}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", color: textColor, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 500 }}>Period From</span>
                <Form.Select value={carryforward.periodFrom} name="periodFrom" onChange={handlePeriodChange}
                  style={{ width: "150px", padding: "10px 14px", border: `1px solid ${borderColor}`, borderRadius: "10px", backgroundColor: selectBackground, color: textColor, fontWeight: 500, cursor: "pointer" }}>
                  <option value="">Select start</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString("default", { month: "long" })}</option>
                  ))}
                </Form.Select>
                <span style={{ fontWeight: 500 }}>To</span>
                <Form.Select value={carryforward.periodTo} name="periodTo" onChange={handlePeriodChange}
                  style={{ width: "150px", padding: "10px 14px", border: `1px solid ${borderColor}`, borderRadius: "10px", backgroundColor: selectBackground, color: textColor, fontWeight: 500, cursor: "pointer" }}>
                  <option value="">Select end</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString("default", { month: "long" })}</option>
                  ))}
                </Form.Select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ color: textColor, fontWeight: 500 }}>Leave Period</span>
                <Form.Select value={leaveData.leavePeriod} name="leavePeriod" onChange={handleChange}
                  style={{ width: "130px", padding: "10px 14px", border: `1px solid ${borderColor}`, borderRadius: "10px", backgroundColor: selectBackground, color: textColor, fontWeight: 500, cursor: "pointer" }}>
                  <option value="1">Yearly</option>
                  <option value="2">Monthly</option>
                </Form.Select>
              </div>
              <div style={{ marginLeft: "auto" }}>
                <ButtonCom type="button" class_name="universal-btn"
                  text={saveAction === "saveSettings" ? "Saving..." : "Save Settings"}
                  url_or_onclick={handleUpdate} icon={<PiPencilSimpleLineBold />} />
              </div>
            </div>

            {/* ── Leave Types & Balances ─────────────────────────────────────── */}
            <div style={{ marginBottom: "40px" }}>
              <h3 style={{ color: textColor, marginBottom: "24px", fontWeight: 700, fontSize: "1.5rem" }}>
                Leave Types & Balances
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "20px" }}>
                {leaveList.map((row, idx) => (
                  <div key={row.leaveId || idx} style={{
                    background: cardBackground, borderRadius: "14px",
                    padding: "18px", border: `1px solid ${borderColor}`, position: "relative",
                  }}>
                    <div onClick={() => handleDelete(row.leaveId)} style={{
                      position: "absolute", top: "12px", right: "12px", width: "32px", height: "32px",
                      borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer", background: isDark ? "rgba(239,68,68,0.1)" : "rgba(239,68,68,0.05)", zIndex: 10,
                    }}>
                      <MdDelete style={{ color: "#ef4444", fontSize: "18px" }} />
                    </div>
                    {editingLeaveId === row.leaveId ? (
                      <input type="text" value={editingLeaveName} onChange={handleLeaveNameChange}
                        onBlur={() => handleLeaveNameBlur(idx)} onKeyPress={(e) => handleLeaveNameKeyPress(e, idx)}
                        autoFocus style={{
                          margin: 0, marginBottom: "6px", color: lightTextColor, fontWeight: 700, fontSize: "1.1rem",
                          background: "transparent", border: `2px solid ${accentColor}`, borderRadius: "6px",
                          padding: "3px 6px", outline: "none", width: "calc(100% - 45px)", display: "block",
                        }} />
                    ) : (
                      <h5 onClick={() => handleLeaveNameClick(row)} style={{
                        margin: 0, marginBottom: "6px", color: lightTextColor, fontWeight: 700, fontSize: "1.1rem",
                        cursor: "pointer", display: "inline-block", padding: "3px 6px", borderRadius: "6px",
                      }}>
                        {row.leaveName}
                      </h5>
                    )}
                    <div style={{ display: "flex", alignItems: "baseline", gap: "6px", marginBottom: "18px" }}>
                      <span style={{ fontSize: "2.5rem", fontWeight: 800, color: accentColor, lineHeight: 1 }}>
                        {row.leaveBalance || "0"}
                      </span>
                      <span style={{ fontSize: "0.95rem", color: mutedTextColor, fontWeight: 500 }}>
                        leaves / {getLeavePeriodText()}
                      </span>
                    </div>
                    <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: "12px" }}>
                      <span style={{ color: mutedTextColor, fontSize: "0.8rem", fontWeight: 500, display: "block", marginBottom: "6px" }}>
                        No. of Balance
                      </span>
                      <InputWithButtons {...tp} value={row.leaveBalance || "0"}
                        onChange={(e) => handelArrayChange(e, idx)} name="leaveBalance" min={0} />
                    </div>
                  </div>
                ))}

                {/* Add card */}
                <div onClick={handleClickOpen} style={{
                  background: addCardBackground, borderRadius: "14px", padding: "18px",
                  border: `2px dashed ${addCardBorder}`, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", minHeight: "220px", cursor: "pointer",
                }}>
                  <div style={{
                    width: "60px", height: "60px", borderRadius: "50%",
                    background: isDark ? "rgba(139,92,246,0.2)" : "rgba(124,58,237,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "12px",
                  }}>
                    <AddIcon style={{ fontSize: "32px", color: accentColor }} />
                  </div>
                  <h5 style={{ color: accentColor, fontWeight: 700, fontSize: "1.1rem", marginBottom: "6px" }}>
                    Add Leave Type
                  </h5>
                  <p style={{ color: mutedTextColor, fontSize: "0.85rem", textAlign: "center", margin: 0 }}>
                    Click to create a new leave category
                  </p>
                </div>
              </div>
            </div>

            {/* ── Special Rules ──────────────────────────────────────────────── */}
            <div style={{
              background: cardBackground, borderRadius: "16px", padding: "28px",
              border: `1px solid ${sectionBorderColor}`, marginBottom: "40px",
            }}>
              <h3 style={{ color: textColor, margin: 0, fontWeight: 700, fontSize: "1.5rem", marginBottom: "6px" }}>
                Special Rules
              </h3>
              <p style={{ color: mutedTextColor, fontSize: "0.88rem", marginBottom: "28px", marginTop: "4px" }}>
                Configure specialised leave entitlements with detailed rules for each policy type.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                {/* ── Menstrual Leave ──────────────────────────────────────────── */}
                <SpecialLeaveCard
                  title="Menstrual Leave"
                  subtitle="Automatically applied for female employees"
                  badge="♀ Female Only"
                  badgeColor={{ bg: isDark ? "rgba(236,72,153,0.18)" : "rgba(236,72,153,0.1)", text: "#db2777" }}
                  enabled={menstrualEnabled}
                  onToggle={(e) => setMenstrualEnabled(e.target.checked)}
                  cardBackground={cardBackground} innerBackground={innerBackground}
                  borderColor={borderColor} textColor={textColor}
                  mutedTextColor={mutedTextColor} accentColor={accentColor} isDark={isDark}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "16px", marginBottom: "4px" }}>
                    <FieldRow label="Number of leaves" mutedTextColor={mutedTextColor}>
                      <SmallNumInput value={menstrualDays || "0"} onChange={setMenstrualDays} {...snProps} />
                    </FieldRow>
                    <FieldRow label="Max leave per cycle" mutedTextColor={mutedTextColor}>
                      <SmallNumInput value={menstrualMaxPerCycle} onChange={setMenstrualMaxPerCycle} {...snProps} />
                    </FieldRow>
                  </div>
                  <SectionDivider label="Leave rules" borderColor={borderColor} mutedTextColor={mutedTextColor} />
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "0" }}>
                    <ToggleRow label="Leave reset cycle"      value={menstrualResetCycle}       onChange={setMenstrualResetCycle}       {...trProps} />
                    <ToggleRow label="Half day allowed"       value={menstrualHalfDay}           onChange={setMenstrualHalfDay}          {...trProps} />
                    <ToggleRow label="Carry forward allowed"  value={menstrualCarryForward}      onChange={setMenstrualCarryForward}     {...trProps} />
                    <ToggleRow label="Encashment allowed"     value={menstrualEncashment}        onChange={setMenstrualEncashment}       {...trProps} />
                    <ToggleRow label="Leave clubbing allowed" value={menstrualClubbingAllowed}   onChange={setMenstrualClubbingAllowed}  {...trProps} />
                    <ToggleRow label="Approval required"      value={menstrualApprovalRequired}  onChange={setMenstrualApprovalRequired} {...trProps} />
                    <ToggleRow label="Reason mandatory"       value={menstrualReasonMandatory}   onChange={setMenstrualReasonMandatory}  {...trProps} />
                  </div>
                </SpecialLeaveCard>

                {/* ── Maternity Leave ──────────────────────────────────────────── */}
             {/* ── Maternity Leave ──────────────────────────────────────────── */}
<SpecialLeaveCard
  title="Maternity Leave"
  subtitle="Automatically applied for female employees"
  badge="♀ Female Only"
  badgeColor={{ bg: isDark ? "rgba(236,72,153,0.18)" : "rgba(236,72,153,0.1)", text: "#db2777" }}
  enabled={maternityEnabled}
  onToggle={(e) => setMaternityEnabled(e.target.checked)}
  cardBackground={cardBackground} innerBackground={innerBackground}
  borderColor={borderColor} textColor={textColor}
  mutedTextColor={mutedTextColor} accentColor={accentColor} isDark={isDark}
>
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "16px", marginBottom: "4px" }}>
    <FieldRow label="Leave duration (weeks)" mutedTextColor={mutedTextColor}>
      <SmallNumInput value={maternityDurationWeeks} onChange={setMaternityDurationWeeks} {...snProps} />
    </FieldRow>
    <FieldRow label="Applied for no. of children" mutedTextColor={mutedTextColor}>
      <SmallNumInput value={maternityNumChildren} onChange={setMaternityNumChildren} {...snProps} />
    </FieldRow>
    <FieldRow label="Pre-delivery leave (days)" mutedTextColor={mutedTextColor}>
      <SmallNumInput value={maternityPreDelivery} onChange={setMaternityPreDelivery} {...snProps} />
    </FieldRow>
    
    {/* Salary Info Row - Replaces paid/salary fields */}
    <div style={{ gridColumn: "span 1" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
        <span style={{ color: mutedTextColor, fontSize: "0.75rem", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Salary during leave
        </span>
        <div style={{ 
          display: "inline-flex", alignItems: "center", gap: "6px",
          background: isDark ? "rgba(139,92,246,0.12)" : "rgba(124,58,237,0.08)",
          padding: "4px 10px", borderRadius: "20px",
          border: `1px solid ${accentColor}33`
        }}>
          <span style={{ fontSize: "0.9rem" }}>💰</span>
          <span style={{ color: accentColor, fontSize: "0.75rem", fontWeight: 500 }}>Payroll Managed</span>
          <div style={{ 
            cursor: "help", position: "relative", display: "inline-flex",
            background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
            borderRadius: "50%", width: "16px", height: "16px", alignItems: "center", justifyContent: "center"
          }} title="Salary during maternity leave is calculated based on payroll configuration and company policy">
            <span style={{ fontSize: "0.7rem" }}>ⓘ</span>
          </div>
        </div>
      </div>
      <div style={{ color: mutedTextColor, fontSize: "0.7rem", marginTop: "4px" }}>
        As per company payroll policy
      </div>
    </div>
  </div>

  <SectionDivider label="Leave rules" borderColor={borderColor} mutedTextColor={mutedTextColor} />
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "0" }}>
    <ToggleRow label="Post delivery auto allocation" value={maternityPostAutoAlloc}    onChange={setMaternityPostAutoAlloc}    {...trProps} />
    <ToggleRow label="Carry forward allowed"        value={maternityCarryForward}     onChange={setMaternityCarryForward}     {...trProps} />
    <ToggleRow label="Encashment allowed"           value={maternityEncashment}       onChange={setMaternityEncashment}       {...trProps} />
    <ToggleRow label="Leave clubbing allowed"       value={maternityClubbingAllowed}  onChange={setMaternityClubbingAllowed}  {...trProps} />
  </div>

  <SectionDivider label="Documentation" borderColor={borderColor} mutedTextColor={mutedTextColor} />
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "0" }}>
    <ToggleRow label="Medical certificate required" value={maternityMedCertRequired}  onChange={setMaternityMedCertRequired}  {...trProps} />
    <ToggleRow label="Document upload mandatory"   value={maternityDocUpload}         onChange={setMaternityDocUpload}        {...trProps} />
    <ToggleRow label="Approval required"           value={maternityApprovalRequired}  onChange={setMaternityApprovalRequired} {...trProps} />
  </div>
</SpecialLeaveCard>

                {/* ── Paternity Leave ──────────────────────────────────────────── */}
            {/* ── Paternity Leave ──────────────────────────────────────────── */}
<SpecialLeaveCard
  title="Paternity Leave"
  subtitle="Applicable for male employees"
  badge="♂ Male Only"
  badgeColor={{ bg: isDark ? "rgba(59,130,246,0.18)" : "rgba(59,130,246,0.1)", text: "#2563eb" }}
  enabled={paternityEnabled}
  onToggle={(e) => setPaternityEnabled(e.target.checked)}
  cardBackground={cardBackground} innerBackground={innerBackground}
  borderColor={borderColor} textColor={textColor}
  mutedTextColor={mutedTextColor} accentColor={accentColor} isDark={isDark}
>
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "16px", marginBottom: "4px" }}>
    <FieldRow label="Total leaves (days)" mutedTextColor={mutedTextColor}>
      <SmallNumInput value={paternityTotalDays} onChange={setPaternityTotalDays} {...snProps} />
    </FieldRow>
    <FieldRow label="Usage window (days after childbirth)" mutedTextColor={mutedTextColor}>
      <SmallNumInput value={paternityUsageWindow} onChange={setPaternityUsageWindow} {...snProps} />
    </FieldRow>
    
    {/* Salary Info Row - Replaces paid/salary fields */}
    <div style={{ gridColumn: "span 1" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
        <span style={{ color: mutedTextColor, fontSize: "0.75rem", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Salary during leave
        </span>
        <div style={{ 
          display: "inline-flex", alignItems: "center", gap: "6px",
          background: isDark ? "rgba(139,92,246,0.12)" : "rgba(124,58,237,0.08)",
          padding: "4px 10px", borderRadius: "20px",
          border: `1px solid ${accentColor}33`
        }}>
          <span style={{ fontSize: "0.9rem" }}>💰</span>
          <span style={{ color: accentColor, fontSize: "0.75rem", fontWeight: 500 }}>Payroll Managed</span>
          <div style={{ 
            cursor: "help", position: "relative", display: "inline-flex",
            background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
            borderRadius: "50%", width: "16px", height: "16px", alignItems: "center", justifyContent: "center"
          }} title="Salary during paternity leave is calculated based on payroll configuration and company policy">
            <span style={{ fontSize: "0.7rem" }}>ⓘ</span>
          </div>
        </div>
      </div>
      <div style={{ color: mutedTextColor, fontSize: "0.7rem", marginTop: "4px" }}>
        As per company payroll policy
      </div>
    </div>
  </div>

  <SectionDivider label="Leave rules" borderColor={borderColor} mutedTextColor={mutedTextColor} />
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "0" }}>
    <ToggleRow label="Continuous leave required" value={paternityContinuousRequired} onChange={setPaternityContinuousRequired} {...trProps} />
    <ToggleRow label="Split leave allowed"       value={paternitySplitAllowed}       onChange={setPaternitySplitAllowed}       {...trProps} />
    <ToggleRow label="Carry forward allowed"     value={paternityCarryForward}       onChange={setPaternityCarryForward}       {...trProps} />
    <ToggleRow label="Encashment allowed"        value={paternityEncashment}         onChange={setPaternityEncashment}         {...trProps} />
    <ToggleRow label="Leave clubbing allowed"    value={paternityClubbingAllowed}    onChange={setPaternityClubbingAllowed}    {...trProps} />
  </div>

  <SectionDivider label="Documentation" borderColor={borderColor} mutedTextColor={mutedTextColor} />
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "0" }}>
    <ToggleRow label="Medical certificate required" value={paternityMedCertRequired}  onChange={setPaternityMedCertRequired}  {...trProps} />
    <ToggleRow label="Document upload mandatory"    value={paternityDocUpload}         onChange={setPaternityDocUpload}        {...trProps} />
    <ToggleRow label="Approval required"            value={paternityApprovalRequired}  onChange={setPaternityApprovalRequired} {...trProps} />
  </div>
</SpecialLeaveCard>

              </div>

              {/* Info note */}
              <div style={{
                marginTop: "24px", padding: "14px 18px",
                background: isDark ? "rgba(139,92,246,0.07)" : "rgba(124,58,237,0.04)",
                borderRadius: "10px", border: `1px dashed ${accentColor}44`,
                display: "flex", alignItems: "flex-start", gap: "10px",
              }}>
                <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>ℹ️</span>
                <span style={{ color: mutedTextColor, fontSize: "0.82rem", lineHeight: 1.6 }}>
                  Special leave policies are allocated{" "}
                  <strong style={{ color: lightTextColor }}>in addition to</strong> standard leave balances.
                  Gender-specific leaves are automatically applied based on the employee's profile. Remember to click{" "}
                  <strong style={{ color: accentColor }}>Save Settings</strong> to apply any changes.
                </span>
              </div>
            </div>

            {/* ── Advanced Leave Rules ───────────────────────────────────────── */}
            <div style={{
              background: cardBackground, borderRadius: "16px", padding: "28px",
              border: `1px solid ${sectionBorderColor}`, marginBottom: "40px",
            }}>
              <h3 style={{ color: textColor, marginBottom: "28px", fontWeight: 700, fontSize: "1.5rem" }}>
                Advanced Leave Rules
              </h3>

              {/* Carryforward toggle */}
              <div style={{ marginBottom: "24px" }}>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "16px 20px", background: innerBackground, borderRadius: "12px",
                  border: `1px solid ${combinedRulesActive ? disabledBorder : borderColor}`,
                  opacity: combinedRulesActive ? 0.45 : 1,
                  pointerEvents: combinedRulesActive ? "none" : "auto",
                  marginBottom: carryforwardChecked && !combinedRulesActive ? "12px" : "0",
                }}>
                  <div>
                    <span style={{ fontSize: "1.15rem", color: textColor, fontWeight: 600, display: "block", marginBottom: "4px" }}>
                      Allow Leave Carryforward
                    </span>
                    <span style={{ fontSize: "0.85rem", color: combinedRulesActive ? "#f97316" : mutedTextColor }}>
                      {combinedRulesActive ? "⚠ Disabled — Turn off Combined Policy to use this" : "Enable employees to carry forward unused leaves"}
                    </span>
                  </div>
                  <Switch checked={carryforwardChecked} onChange={handleToggleCarryforward} disabled={combinedRulesActive}
                    sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: accentColor } }} />
                </div>
                {carryforwardChecked && !combinedRulesActive && (
                  <div style={{ background: innerBackground, padding: "24px", borderRadius: "12px", border: `1px solid ${switchBorderColor}`, marginLeft: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", padding: "12px 16px", background: cardBackground, borderRadius: "10px" }}>
                      <span style={{ color: lightTextColor, fontWeight: 500 }}>Carry forward all unused leaves</span>
                      <Switch checked={allCarryforwardChecked} onChange={handleToggleAllCarryforward} size="small"
                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: accentColor } }} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", color: lightTextColor, padding: "12px 16px", background: cardBackground, borderRadius: "10px", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 500 }}>Maximum carry-forward limit:</span>
                      <InputWithButtons {...tp} value={carryforward.allowMaxLeaveCarryForward || "0"}
                        onChange={handleLeaveChanges} name="allowMaxLeaveCarryForward" placeholder="0" min={0}
                        showUnitDropdown={true} unit={carryforwardUnit}
                        onUnitChange={(e) => setCarryforwardUnit(e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              {/* Encashment toggle */}
              <div>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "16px 20px", background: innerBackground, borderRadius: "12px",
                  border: `1px solid ${combinedRulesActive ? disabledBorder : borderColor}`,
                  opacity: combinedRulesActive ? 0.45 : 1,
                  pointerEvents: combinedRulesActive ? "none" : "auto",
                  marginBottom: encashmentChecked && !combinedRulesActive ? "12px" : "0",
                }}>
                  <div>
                    <span style={{ fontSize: "1.15rem", color: textColor, fontWeight: 600, display: "block", marginBottom: "4px" }}>
                      Allow Leave Encashment
                    </span>
                    <span style={{ fontSize: "0.85rem", color: combinedRulesActive ? "#f97316" : mutedTextColor }}>
                      {combinedRulesActive ? "⚠ Disabled — Turn off Combined Policy to use this" : "Enable employees to encash unused paid leaves"}
                    </span>
                  </div>
                  <Switch checked={encashmentChecked} onChange={handleToggleEncashment} disabled={combinedRulesActive}
                    sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: accentColor } }} />
                </div>
                {encashmentChecked && !combinedRulesActive && (
                  <div style={{ background: innerBackground, padding: "24px", borderRadius: "12px", border: `1px solid ${switchBorderColor}`, marginLeft: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", padding: "12px 16px", background: cardBackground, borderRadius: "10px" }}>
                      <span style={{ color: lightTextColor, fontWeight: 500 }}>Encash all unused paid leaves</span>
                      <Switch checked={allEncashmentChecked} onChange={handleToggleAllEncashment} size="small"
                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: accentColor } }} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", color: lightTextColor, padding: "12px 16px", background: cardBackground, borderRadius: "10px", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 500 }}>Maximum encashment limit:</span>
                      <InputWithButtons {...tp} value={carryforward.allowMaxLeaveEncashment || "0"}
                        onChange={handleLeaveChanges} name="allowMaxLeaveEncashment" placeholder="0" min={0}
                        showUnitDropdown={true} unit={encashmentUnit}
                        onUnitChange={(e) => setEncashmentUnit(e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              {/* OR divider */}
              <div style={{ display: "flex", alignItems: "center", margin: "32px 0" }}>
                <div style={{ flex: 1, height: "1px", background: borderColor }} />
                <div style={{ margin: "0 20px", padding: "8px 20px", background: isDark ? "#2a2342" : "#f0f4f8", borderRadius: "20px", border: `1px solid ${borderColor}` }}>
                  <span style={{ color: mutedTextColor, fontWeight: 600, fontSize: "0.9rem" }}>OR</span>
                </div>
                <div style={{ flex: 1, height: "1px", background: borderColor }} />
              </div>

              {/* Combined Rules */}
              <div>
                <h4 style={{ color: textColor, marginBottom: "20px", fontWeight: 600, fontSize: "1.1rem" }}>
                  Combined Rules
                </h4>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "16px 20px", background: innerBackground, borderRadius: "12px",
                  border: `1px solid ${individualRulesActive ? disabledBorder : borderColor}`,
                  opacity: individualRulesActive ? 0.45 : 1,
                  pointerEvents: individualRulesActive ? "none" : "auto",
                  marginBottom: combinedRulesChecked && !individualRulesActive ? "12px" : "0",
                }}>
                  <div>
                    <span style={{ fontSize: "1.15rem", color: textColor, fontWeight: 600, display: "block", marginBottom: "4px" }}>
                      Combined Policy
                    </span>
                    <span style={{ fontSize: "0.85rem", color: individualRulesActive ? "#f97316" : mutedTextColor }}>
                      {individualRulesActive
                        ? "⚠ Disabled — Turn off individual rules above to use this"
                        : "Design a combined policy for leave carryforward and encashment"}
                    </span>
                  </div>
                  <Switch checked={combinedRulesChecked} onChange={handleToggleCombinedRules} disabled={individualRulesActive}
                    sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: accentColor } }} />
                </div>
                {combinedRulesChecked && !individualRulesActive && (
                  <div style={{ background: innerBackground, padding: "24px", borderRadius: "12px", border: `1px solid ${switchBorderColor}`, marginLeft: "20px" }}>
                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                      <CombinedInputWithDropdown {...tp} label="Leave Carryforward"
                        value={combinedRules.carryforwardValue} unit={combinedRules.carryforwardUnit}
                        onValueChange={(v) => handleCombinedRulesChange("carryforwardValue", v)}
                        onUnitChange={(u) => handleCombinedRulesChange("carryforwardUnit", u)} />
                      <CombinedInputWithDropdown {...tp} label="Leave Encashment"
                        value={combinedRules.encashmentValue} unit={combinedRules.encashmentUnit}
                        onValueChange={(v) => handleCombinedRulesChange("encashmentValue", v)}
                        onUnitChange={(u) => handleCombinedRulesChange("encashmentUnit", u)} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Delete Confirmation Modal ──────────────────────────────────── */}
            <Modal show={show} onHide={handleModalClose} backdrop="static" centered>
              <Modal.Header closeButton style={{ backgroundColor: cardBackground, borderBottom: `1px solid ${borderColor}`, color: textColor }}>
                <Modal.Title style={{ fontWeight: 700 }}>Delete Leave Type</Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ backgroundColor: cardBackground, color: textColor, padding: "24px" }}>
                Are you sure you want to delete this leave type? This action cannot be undone.
              </Modal.Body>
              <Modal.Footer style={{ backgroundColor: cardBackground, borderTop: `1px solid ${borderColor}` }}>
                <Button style={{ backgroundColor: isDark ? "#2a2342" : "#e2e8f0", color: isDark ? "#fff" : "#1a202c", marginRight: "8px", padding: "10px 20px", borderRadius: "10px", border: "none", fontWeight: 600 }} onClick={handleModalClose}>
                  Cancel
                </Button>
                <Button style={{ backgroundColor: "#ef4444", color: "#fff", padding: "10px 20px", borderRadius: "10px", border: "none", fontWeight: 600 }} onClick={handleDeleteSubmit}>
                  Delete
                </Button>
              </Modal.Footer>
            </Modal>

            {/* ── Add / Edit Leave Modal ─────────────────────────────────────── */}
            <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
              <DialogTitle style={{ backgroundColor: cardBackground, color: textColor, borderBottom: `1px solid ${borderColor}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: textColor, fontWeight: 700 }}>
                    {isEdit ? "Edit Leave Type" : "Add New Leave Type"}
                  </span>
                  <button className="universal-btn" type="button" onClick={preSubmit}>
                    {isEdit ? "Update Leave" : "Add Leave"}
                  </button>
                </div>
              </DialogTitle>
              <div style={{ overflow: "auto", backgroundColor: cardBackground }}>
                <Form noValidate validated={validated} onSubmit={isEdit ? handleEditSubmit : handleSubmit}>
                  <DialogContent style={{ backgroundColor: cardBackground, padding: "24px" }}>
                    <Row>
                      <Col sm="12">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: textColor, fontWeight: 600 }}>
                            Leave Name <span style={{ color: "#ef4444" }}> *</span>
                          </Form.Label>
                          <Form.Control type="text" name="leaveName" value={leaveData.leaveName || ""}
                            placeholder="Enter Leave Name" onChange={handleChange} required
                            style={{ backgroundColor: inputBackground, color: textColor, padding: "12px 16px", borderRadius: "10px", border: `2px solid ${borderColor}`, outline: "none" }} />
                          <Form.Control.Feedback type="invalid">Please provide valid leave name.</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col sm="12">
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: textColor, fontWeight: 600 }}>
                            Leave Balance <span style={{ color: "#ef4444" }}> *</span>
                          </Form.Label>
                          <Form.Control type="number" name="leaveBalance" value={leaveData.leaveBalance || ""}
                            placeholder="Enter Balance" onChange={handleChange} required
                            style={{ backgroundColor: inputBackground, color: textColor, padding: "12px 16px", borderRadius: "10px", border: `2px solid ${borderColor}`, outline: "none" }} />
                          <Form.Control.Feedback type="invalid">Please provide valid value.</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </DialogContent>
                  <DialogActions style={{ backgroundColor: cardBackground, padding: "16px 24px" }}>
                    <Button variant="contained" color="info" type="submit" style={{ display: "none" }} id="newLeaveId">
                      Save &nbsp;
                      {state.commonLoading && <Spinner animation="border" variant="light" size="sm" />}
                    </Button>
                  </DialogActions>
                </Form>
              </div>
            </Dialog>

          </div>
        }
      />
    </>
  );
};

export default LeaveSettings;